import { LanguageToggle, useT } from '@/lib/i18n';
import { PageLoader } from '@/components/ui/loader';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CalendarCheck2, CheckCircle2, ChevronLeft, ChevronRight, Clock, MapPin, Video } from 'lucide-react';
import { Brand, PRODUCT_NAME } from '@/components/shared/brand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError, apiGet, apiPost } from '@/lib/api';
import { cn } from '@/lib/utils';

type PageInfo = {
  name: string;
  description: string | null;
  durationMinutes: number;
  locationType: string;
  location: string | null;
  timezone: string;
  questions: { id: string; label: string; required: boolean }[];
  kind: string;
  company: string;
  hosts: { name: string }[];
  maxDaysAhead: number;
};

type Booked = { startsAt: string; endsAt: string; host: string | null; location: string | null; manageToken: string };

const guestZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Dubai';
const dayKey = (date: Date) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: guestZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
const timeLabel = (iso: string) =>
  new Intl.DateTimeFormat('en-GB', { timeZone: guestZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(iso));
const longDate = (iso: string) =>
  new Intl.DateTimeFormat('en-GB', { timeZone: guestZone, weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(iso));

const WINDOW = 7;

/** dovocrm.com/meet/<slug> — where a customer picks a time. */
export function PublicBookingPage() {
  const t = useT();
  const { slug = '' } = useParams();
  const [offset, setOffset] = useState(0);
  const [day, setDay] = useState<string | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '' });
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [booked, setBooked] = useState<Booked | null>(null);

  const info = useQuery({ queryKey: ['meet', slug], queryFn: () => apiGet<PageInfo>(`/public/meet/${slug}`), retry: false });
  const from = useMemo(() => {
    const date = new Date(Date.now() + offset * WINDOW * 86_400_000);
    return dayKey(date);
  }, [offset]);
  const slots = useQuery({
    queryKey: ['meet', slug, 'slots', from],
    queryFn: () => apiGet<{ timezone: string; slots: string[] }>(`/public/meet/${slug}/slots`, { params: { from, days: WINDOW + 1 } }),
    enabled: info.isSuccess,
  });

  const byDay = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const iso of slots.data?.slots ?? []) {
      const key = dayKey(new Date(iso));
      map.set(key, [...(map.get(key) ?? []), iso]);
    }
    return map;
  }, [slots.data]);

  const days = useMemo(() => {
    const start = new Date(`${from}T12:00:00`);
    return Array.from({ length: WINDOW }, (_, index) => {
      const date = new Date(start.getTime() + index * 86_400_000);
      return { key: dayKey(date), date };
    });
  }, [from]);

  const book = useMutation({
    mutationFn: () =>
      apiPost<Booked>(`/public/meet/${slug}/book`, {
        startsAt: slot,
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        answers,
        timezone: guestZone,
      }),
    onSuccess: setBooked,
    onError: () => void slots.refetch(),
  });

  if (info.isLoading) return <Frame><PageLoader /></Frame>;
  if (info.isError || !info.data) {
    return (
      <Frame>
        <p className="text-center font-semibold">This booking page is not available.</p>
        <p className="mt-1 text-center text-sm text-muted-foreground">The link may have changed — ask whoever sent it for a new one.</p>
      </Frame>
    );
  }
  const page = info.data;

  if (booked) {
    return (
      <Frame>
        <div className="text-center">
          <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
          <h1 className="mt-3 text-2xl font-extrabold">{t('You are booked')}</h1>
          <p className="mt-2 text-muted-foreground">
            {page.name} with {booked.host ?? page.company}
          </p>
          <p className="mt-4 text-lg font-semibold">
            {longDate(booked.startsAt)}, {timeLabel(booked.startsAt)} – {timeLabel(booked.endsAt)}
          </p>
          <p className="text-xs text-muted-foreground">Times shown in {guestZone}</p>
          {booked.location && <p className="mt-3 text-sm">{booked.location}</p>}
          <p className="mt-6 text-sm text-muted-foreground">A confirmation with a calendar invite is on its way to {form.email}.</p>
        </div>
      </Frame>
    );
  }

  const daySlots = day ? byDay.get(day) ?? [] : [];
  const missing =
    form.name.trim().length < 2 ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) ||
    page.questions.some((question) => question.required && !answers[question.id]?.trim());

  return (
    <Frame wide>
      <div className="grid gap-8 md:grid-cols-[16rem_1fr]">
        <div className="space-y-3">
          <p className="text-sm font-semibold text-primary">{page.company}</p>
          <h1 className="text-2xl font-extrabold leading-tight">{page.name}</h1>
          {page.hosts.length > 0 && (
            <p className="text-sm text-muted-foreground">
              {page.kind === 'ROUND_ROBIN' ? 'With someone from the team' : `With ${page.hosts[0].name}`}
            </p>
          )}
          <p className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" /> {page.durationMinutes} {t('minutes')}
          </p>
          <p className="flex items-center gap-2 text-sm">
            {page.locationType === 'video' ? <Video className="h-4 w-4 text-muted-foreground" /> : <MapPin className="h-4 w-4 text-muted-foreground" />}
            {page.locationType === 'in_person'
              ? page.location ?? 'In person'
              : page.locationType === 'phone'
                ? 'Phone call'
                : page.locationType === 'video'
                  ? 'Video call — link in the confirmation'
                  : 'Details in the confirmation'}
          </p>
          {page.description && <p className="whitespace-pre-wrap text-sm text-muted-foreground">{page.description}</p>}
        </div>

        {!slot ? (
          <div>
            <div className="mb-3 flex items-center justify-between">
              <p className="font-semibold">{t('Pick a day')}</p>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => { setOffset(offset - 1); setDay(null); }}>
                  <ChevronLeft />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={(offset + 1) * WINDOW >= page.maxDaysAhead}
                  onClick={() => { setOffset(offset + 1); setDay(null); }}
                >
                  <ChevronRight />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
              {days.map(({ key, date }) => {
                const count = byDay.get(key)?.length ?? 0;
                return (
                  <button
                    key={key}
                    disabled={!count}
                    onClick={() => setDay(key)}
                    className={cn(
                      'rounded-xl border px-1 py-2 text-center text-sm transition-colors',
                      day === key ? 'border-primary bg-primary text-primary-foreground' : count ? 'hover:border-primary/50 hover:bg-accent' : 'opacity-40',
                    )}
                  >
                    <span className="block text-[0.65rem] uppercase">{date.toLocaleDateString('en-GB', { weekday: 'short' })}</span>
                    <span className="block text-lg font-bold">{date.getDate()}</span>
                    <span className="block text-[0.65rem]">{date.toLocaleDateString('en-GB', { month: 'short' })}</span>
                  </button>
                );
              })}
            </div>
            {slots.isLoading && <p className="mt-4 text-sm text-muted-foreground">{t('Finding free times…')}</p>}
            {!slots.isLoading && byDay.size === 0 && <p className="mt-4 text-sm text-muted-foreground">No free times this week — try the next one.</p>}
            {day && (
              <>
                <p className="mb-2 mt-5 font-semibold">{longDate(daySlots[0] ?? `${day}T12:00:00`)}</p>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {daySlots.map((iso) => (
                    <button
                      key={iso}
                      onClick={() => setSlot(iso)}
                      className="rounded-lg border border-primary/30 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                    >
                      {timeLabel(iso)}
                    </button>
                  ))}
                </div>
              </>
            )}
            <p className="mt-4 text-xs text-muted-foreground">{t('Times shown in your time zone')} ({guestZone})</p>
          </div>
        ) : (
          <div className="space-y-3">
            <button className="flex items-center gap-1 text-sm text-primary" onClick={() => setSlot(null)}>
              <ChevronLeft className="h-4 w-4" /> {t('Change time')}
            </button>
            <p className="flex items-center gap-2 rounded-xl bg-primary/5 px-3 py-2 font-semibold">
              <CalendarCheck2 className="h-5 w-5 text-primary" /> {longDate(slot)}, {timeLabel(slot)}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>{t('Your name')} *</Label>
                <Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} autoComplete="name" />
              </div>
              <div className="space-y-1.5">
                <Label>{t('Email')} *</Label>
                <Input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} autoComplete="email" />
              </div>
              <div className="space-y-1.5">
                <Label>{t('Phone')}</Label>
                <Input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} placeholder="+971 …" autoComplete="tel" />
              </div>
              <div className="space-y-1.5">
                <Label>{t('Company')}</Label>
                <Input value={form.company} onChange={(event) => setForm({ ...form, company: event.target.value })} autoComplete="organization" />
              </div>
            </div>
            {page.questions.map((question) => (
              <div key={question.id} className="space-y-1.5">
                <Label>
                  {question.label}
                  {question.required ? ' *' : ''}
                </Label>
                <Textarea rows={3} value={answers[question.id] ?? ''} onChange={(event) => setAnswers({ ...answers, [question.id]: event.target.value })} />
              </div>
            ))}
            <FormError message={book.error instanceof ApiRequestError ? book.error.message : book.error ? 'Booking failed — try again.' : null} />
            <Button className="w-full" size="lg" disabled={missing || book.isPending} onClick={() => book.mutate()}>
              {book.isPending ? t('Booking…') : t('Confirm booking')}
            </Button>
          </div>
        )}
      </div>
    </Frame>
  );
}

function Frame({ children, wide = false }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="relative min-h-screen bg-surface-app px-4 py-10">
      <LanguageToggle className="absolute end-4 top-4" />
      <div className={cn('mx-auto rounded-3xl border bg-card p-6 shadow-xl sm:p-8', wide ? 'max-w-4xl' : 'max-w-lg')}>{children}</div>
      <div className="mx-auto mt-6 flex max-w-lg items-center justify-center gap-2 text-xs text-muted-foreground">
        Scheduling by <Brand size="sm" showTagline={false} className="h-5" /> {PRODUCT_NAME}
      </div>
    </div>
  );
}

type Managed = {
  title: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  location: string | null;
  status: string;
  host: string | null;
  company: string;
};

/** /meet/manage/<token> — the guest's own view of the booking, with cancel. */
export function PublicManageMeetingPage() {
  const { token = '' } = useParams();
  const meeting = useQuery({ queryKey: ['meet', 'manage', token], queryFn: () => apiGet<Managed>(`/public/meetings/${token}`), retry: false });
  const cancel = useMutation({
    mutationFn: () => apiPost<{ status: string }>(`/public/meetings/${token}/cancel`),
    onSuccess: () => void meeting.refetch(),
  });

  if (meeting.isLoading) return <Frame><PageLoader /></Frame>;
  if (!meeting.data) return <Frame><p className="text-center font-semibold">Meeting not found.</p></Frame>;
  const data = meeting.data;
  const upcoming = data.status === 'SCHEDULED' && new Date(data.startsAt).getTime() > Date.now();

  return (
    <Frame>
      <div className="text-center">
        <p className="text-sm font-semibold text-primary">{data.company}</p>
        <h1 className="mt-1 text-2xl font-extrabold">{data.title}</h1>
        <p className="mt-3 text-lg font-semibold">
          {longDate(data.startsAt)}, {timeLabel(data.startsAt)} – {timeLabel(data.endsAt)}
        </p>
        <p className="text-xs text-muted-foreground">Times shown in {guestZone}</p>
        {data.host && <p className="mt-2 text-sm">With {data.host}</p>}
        {data.location && <p className="mt-1 text-sm text-muted-foreground">{data.location}</p>}
        {data.status === 'CANCELLED' ? (
          <p className="mt-6 rounded-xl bg-neutral-100 px-4 py-3 text-sm font-medium">This meeting is cancelled.</p>
        ) : upcoming ? (
          <Button
            variant="outline"
            className="mt-6"
            disabled={cancel.isPending}
            onClick={() => window.confirm('Cancel this meeting?') && cancel.mutate()}
          >
            {cancel.isPending ? 'Cancelling…' : 'Cancel meeting'}
          </Button>
        ) : null}
        <FormError message={cancel.error instanceof ApiRequestError ? cancel.error.message : null} />
      </div>
    </Frame>
  );
}
